import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Button, TextField, MenuItem } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { createAction, fetchDepartment, fetchTasks } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";
import BuildDepartmentDrawer from "../../components/build/BuildDepartmentDrawer";

export default function BuildDepartmentActionCreatePage() {
  const { businessDomainId, businessUnitId, departmentId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [form, setForm] = useState({ actionName: "", actionDescription: "", url: "" });
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [department, setDepartment] = useState(null);
  const [saving, setSaving] = useState(false);

  const listPath = `/build/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}/actions`;

  const loadData = useCallback(async () => {
    try {
      const dept = await fetchDepartment(departmentId);
      setDepartment(dept);
      if (dept.jobId) {
        const taskList = await fetchTasks(dept.jobId);
        setTasks(Array.isArray(taskList) ? taskList : []);
      }
    } catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTask) { notify.error(t("action.error.noTask")); return; }
    setSaving(true);
    try {
      await createAction({
        companyId: COMPANY_ID,
        departmentId,
        departmentName: department?.departmentName || "",
        taskId: selectedTask.taskId,
        taskName: selectedTask.taskName,
        actionName: form.actionName,
        actionDescription: form.actionDescription,
        url: form.url,
      });
      notify.success(t("message.create.success"));
      navigate(listPath, { replace: true });
    } catch (err) { notify.apiError(err); }
    finally { setSaving(false); }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <BuildDepartmentDrawer />
        <Typography variant="h6">{t("build.department.action.create.title")}</Typography>
      </Box>
      <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 600 }}>
        <TextField
          select
          label={t("action.task")}
          value={selectedTask?.taskId || ""}
          onChange={(e) => setSelectedTask(tasks.find((tk) => tk.taskId === e.target.value) || null)}
          required
          fullWidth
        >
          {tasks.map((tk) => (
            <MenuItem key={tk.taskId} value={tk.taskId}>{tk.taskName}</MenuItem>
          ))}
        </TextField>
        <TextField
          name="actionName"
          label={t("action.name")}
          value={form.actionName}
          onChange={handleChange}
          required
          fullWidth
        />
        <TextField
          name="actionDescription"
          label={t("common.description")}
          value={form.actionDescription}
          onChange={handleChange}
          multiline
          rows={3}
          fullWidth
        />
        <TextField
          name="url"
          label={t("common.url")}
          value={form.url}
          onChange={handleChange}
          fullWidth
        />
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          <Button type="submit" variant="contained" disabled={saving}>
            {t("common.add")}
          </Button>
          <Button variant="outlined" onClick={() => navigate(`/build/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}/actions`)}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
